/**
 * Wins split by the Martingale rung they landed on.
 *
 * WHY THIS IS ITS OWN FUNCTION
 *
 * Adlain asked for exactly this breakdown: how many signals win outright, and
 * how many are only rescued at MG1, MG2 or MG3. It is a number he will read and
 * act on, so it is worth testing rather than merely compiling - which an inline
 * expression inside a React component is not.
 *
 * WHY THE SPLIT MATTERS AT ALL
 *
 * A headline win rate hides it, and the two are very different propositions. A
 * strategy whose wins are mostly rescues carries far more risk per win, because
 * each rescue was reached by staking several times the base amount. Measured on
 * this project's own data: 35 of 39 cycles "recovered" - a 90% recovery rate -
 * while the net came out three times WORSE than flat staking, because four
 * busts at 16.5x undid every recovery at 1x.
 *
 * WHICH FIELD THIS GROUPS BY, AND WHY IT IS NOT `martingaleStep`
 *
 * `martingaleStep` is written AFTER settlement: a win RESETS the ladder to 0
 * and a loss ADVANCES it. So every win in the ledger sits at step 0 regardless
 * of the rung it actually won on. Measured on the client's live data
 * 2026-09-28 (16 records): WIN/0, LOSS/1, LOSS/2 only - grouping by it would
 * report "No MG: 5/5 wins, MG1: 0, MG2: 0", i.e. a flat 100% first-entry rate
 * that is not true. `placedAtStep` (bot.ts:1244, captured before settlement
 * overwrites the state) is the rung the trade was PLACED at, which is the one
 * a reader means. Same data, grouped by it:
 *
 *     WIN placed at 0 -> 3     won on the first entry
 *     WIN placed at 1 -> 3     rescued at MG1
 *     WIN placed at 2 -> 2     rescued at MG2
 */

export interface MgTrade {
  outcome: string;
  /** 0 = no Martingale, 1 = MG1, and so on. Written AFTER settlement. */
  martingaleStep?: number;
  /**
   * The rung this trade was PLACED at. Absent on records written before the
   * field existed, and on rows that never reached placement (SKIPPED).
   */
  placedAtStep?: number;
  profitUsd?: number;
}

export interface MgLevel {
  step: number;
  /**
   * Cycles that REACHED this rung. Every cycle reaches rung 0, so `trades` on
   * the "No MG" row is the cycle count - which is what makes "6 / 10" read the
   * way the client asked for it.
   */
  trades: number;
  wins: number;
  net: number;
  /** Null rather than 0 when nothing settled, so "no data" cannot read as 0%. */
  winRate: number | null;
}

/**
 * Ties are EXCLUDED, not counted either way.
 *
 * Pocket Option refunds a tie, so it is neither a win nor a loss. Counting it
 * as a loss understates the rate and counting it as a win flatters it; both
 * move a number the client is using to judge the strategy.
 */
export function byMartingaleLevel(items: MgTrade[]): MgLevel[] {
  const rows = new Map<number, MgLevel>();

  for (const r of items) {
    const o = String(r.outcome ?? '').toLowerCase();
    if (o === 'tie' || o === 'draw') continue;
    if (o !== 'win' && o !== 'loss') continue; // pending, unknown: not settled

    // A record with no placedAtStep predates the field (or never reached
    // placement). It is SKIPPED rather than defaulted to 0: defaulting would
    // file it under "No MG" and inflate the first-entry count - the exact
    // number the client reads. Better a smaller honest sample.
    const raw = Number(r.placedAtStep);
    if (!Number.isFinite(raw) || raw < 0) continue;
    const step = Math.floor(raw);

    const cur = rows.get(step) ?? { step, trades: 0, wins: 0, net: 0, winRate: null };
    cur.trades++;
    if (o === 'win') cur.wins++;
    cur.net += Number.isFinite(Number(r.profitUsd)) ? Number(r.profitUsd) : 0;
    rows.set(step, cur);
  }

  const out = [...rows.values()].sort((a, b) => a.step - b.step);
  for (const r of out) r.winRate = r.trades > 0 ? r.wins / r.trades : null;
  return out;
}
